import { executeGraphQL, executeGraphQLWithAuth, GraphQLAuthMode } from './graphql';
import { DocumentNode } from 'graphql';
import { print } from 'graphql';

/**
 * Helper function to create a GraphQL query
 * @param queryString - GraphQL query string or DocumentNode (from gql template tag)
 * @param variables - Variables for the query
 * @param authMode - Authentication mode ('api-key', 'cognito', or 'auto')
 * @param getAccessToken - Optional callback to get Cognito ID token
 * @returns Promise resolving to the response data
 */
export async function query<T = unknown>(
  queryString: string | DocumentNode,
  variables?: Record<string, unknown>,
  authMode: GraphQLAuthMode = 'auto',
  getAccessToken?: () => Promise<string | null>,
): Promise<T> {
  const queryStr = typeof queryString === 'string' ? queryString : print(queryString);
  return executeGraphQL<T>(queryStr, variables, authMode, getAccessToken);
}

/**
 * Helper function to create a GraphQL mutation
 * @param mutationString - GraphQL mutation string or DocumentNode (from gql template tag)
 * @param variables - Variables for the mutation
 * @param authMode - Authentication mode ('api-key', 'cognito', or 'auto')
 * @param getAccessToken - Optional callback to get Cognito ID token
 * @returns Promise resolving to the response data
 */
export async function mutation<T = unknown>(
  mutationString: string | DocumentNode,
  variables?: Record<string, unknown>,
  authMode: GraphQLAuthMode = 'auto',
  getAccessToken?: () => Promise<string | null>,
): Promise<T> {
  const mutationStr = typeof mutationString === 'string' ? mutationString : print(mutationString);
  return executeGraphQL<T>(mutationStr, variables, authMode, getAccessToken);
}

/**
 * Helper function to create a GraphQL query with automatic Cognito authentication
 * @param queryString - GraphQL query string or DocumentNode (from gql template tag)
 * @param variables - Variables for the query
 * @param getAccessToken - Callback to get Cognito access token
 * @param requireAuth - Whether authentication is required (default: false)
 * @returns Promise resolving to the response data
 */
export async function queryWithAuth<T = unknown>(
  queryString: string | DocumentNode,
  variables?: Record<string, unknown>,
  getAccessToken?: () => Promise<string | null>,
  requireAuth = false,
): Promise<T> {
  const queryStr = typeof queryString === 'string' ? queryString : print(queryString);
  return executeGraphQLWithAuth<T>(queryStr, variables, getAccessToken, requireAuth);
}

/**
 * Helper function to create a GraphQL mutation with automatic Cognito authentication
 * @param mutationString - GraphQL mutation string or DocumentNode (from gql template tag)
 * @param variables - Variables for the mutation
 * @param getAccessToken - Callback to get Cognito access token
 * @param requireAuth - Whether authentication is required (default: true for mutations)
 * @returns Promise resolving to the response data
 */
export async function mutationWithAuth<T = unknown>(
  mutationString: string | DocumentNode,
  variables?: Record<string, unknown>,
  getAccessToken?: () => Promise<string | null>,
  requireAuth = true,
): Promise<T> {
  const mutationStr = typeof mutationString === 'string' ? mutationString : print(mutationString);
  return executeGraphQLWithAuth<T>(mutationStr, variables, getAccessToken, requireAuth);
}

/**
 * Re-export GraphQL utilities for convenience
 */
export { gql } from 'graphql-tag';
export {
  GraphQLRequestError,
  isGraphQLError,
  getGraphQLErrorMessage,
  isAuthError,
} from './graphql';
export type { GraphQLAuthMode } from './graphql';
